// The farm market: tabs (Wszystko / Warzywa / Owoce / Zboża / Kwiaty, then the rest), pages of 8 crop cards
// with arrows, each card a picture story — seedling (seed cost, planting XP) → time → ripe crop (sell
// price, harvest XP) — then mastery stars and progress, and a lock for crops above the player's level (farmMarket.mjs decides all of it).
// Resolves with the chosen crop id (sticky selection lives in the scene) or null.
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { lockInput, unlockInput } from '../ui/inputLock.js';
import { trackPanel } from '../ui/panelSwitch.js';
import { ensureLoaded } from '../ui/sceneLoading.js';
import { showModal } from '../ui/Modal.js';
import { gamePanel, gameButton, gameCard, cardFeedback, woodTitle, pageArrows } from '../ui/gameUi.js';
import { tapGate } from '../ui/tapGate.mjs';
import { createBuyQueue } from './buyQueue.mjs';
import { fuelNow, humanizeRemaining, cropArtId } from './farmRules.mjs';
import { CROP_TINT, vehicleTexture } from './farmPlaceholders.js';
import { TABS, TAB_ROWS, animalCards, marketPage, toolCards, cropCardFoot, objectCards, treeCardInfo, fitScale, marketKey, tabIsNew, markTabSeen, siteStorage, colorDots, pickedColor, savePickedColor } from './farmMarket.mjs';
import { TOOLS, vehicleKey, plural } from './vehicles.mjs';
import { toolCursor } from './farmCursorApply.js';
import { drawDrop } from './farmFxArt.js';
import { iconText } from '../ui/iconText.js';
import { objectTextureKey } from './farmObjects.mjs';
import { isTouchDevice } from '../ui/isTouchDevice.js';
import { serverNow } from '../state/serverClock.mjs';

const FONT = 'system-ui, -apple-system, sans-serif';
const COLS = 4, CARD_W = 210, CARD_H = 190, GAP = 14;
const PANEL_W = 1120, PANEL_H = 640;
// Phones show the market at ~0.47 scale, where its 13-18 px card text came out 4-8 CSS px. The
// panel already fills the screen, so on touch the text itself is drawn a quarter bigger.
const FONT_SCALE = isTouchDevice() ? 1.3 : 1;
const fnum = (n) => Math.round(n * FONT_SCALE);
const fpx = (n) => `${fnum(n)}px`;
// Phones: one row of three cards, drawn bigger (the body is scaled about the row, see draw()),
// and more pages — the owner's call (2026-09-30) over two cramped rows.
const TOUCH_ROW = FONT_SCALE > 1;
const GRID_COLS = TOUCH_ROW ? 3 : COLS;
const PER_PAGE = TOUCH_ROW ? 3 : 8;
const ROW_Y = TOUCH_ROW ? GAME_HEIGHT / 2 + 4 : GAME_HEIGHT / 2 - 98;   // first row's centre
const MAX_GRID_W = 900, MAX_GRID_H = 430, MAX_GRID_SCALE = 1.35;          // wide for the round arrows; 640 tall so the wooden title stays on screen (700 cropped it)
const ARROW_DX = (COLS * CARD_W + (COLS - 1) * GAP) / 2 + 58;   // just outside the card grid
const readyKey = (id) => `farm_crop_${id}_ready`;
const seedlingKey = (id) => `farm_crop_${id}_seedling`;
// Crop card (owner 2026-10-20): reads without words for a 7-year-old — seed cost red, sell price green,
// XP brown, each XP next to the step that pays it. Phones: design 1d (storyParts); desktop: 3a (rowParts).
const CROP_COL = TOUCH_ROW ? 62 : 58, CROP_PIC_W = 66, CROP_PIC_H = 50;
const CROP_RED = '#d4503c', CROP_GREEN = '#3f9a2a', CROP_XP = '#7a5a00', CROP_LOCKED = '#777';
// Flower colours (Kwiaty §7): the dots on the card's name row.
const DOT_HEX = { czerwony: 0xd8322e, zolty: 0xf2c522, rozowy: 0xf28cb8, fioletowy: 0x8a4fc4, bialy: 0xffffff, czarny: 0x262626,
  niebieski: 0x3d7be0, bialorozowy: 0xf7d6e6, fioletowozolty: 0x9a5ec9, czerwonopomaranczowy: 0xe8662a };
const DOT_STEP = TOUCH_ROW ? 22 : 20, DOT_HIT = TOUCH_ROW ? 13 : 10, DOT_RIGHT = 14;   // DOT_RIGHT: last dot's centre from the card's edge

// Phones (design 1d): seedling → time → ripe crop in three columns, coins and XP under each picture.
const XP_Y = 36;          // owner 2026-10-20: at 28 the touch-size coin and star touched (no gap)
function storyParts(scene, c, cx, cy, pic, text, art = c.id) {
  const out = [
    pic(cx - CROP_COL, cy - 34, seedlingKey(art), CROP_PIC_W, CROP_PIC_H), pic(cx + CROP_COL, cy - 34, readyKey(art), CROP_PIC_W, CROP_PIC_H),
    text(cx, cy - 40, '{timer}', '#555', 12, false),
    text(cx, cy - 22, humanizeRemaining(c.growSeconds), '#555', 12, false),
    arrow(scene, cx, cy - 8),
    text(cx - CROP_COL, cy + 8, `${c.seed} {coin}`, CROP_RED),
    text(cx - CROP_COL, cy + XP_Y, `+${c.xp} {xp_star}`, CROP_XP),
    text(cx + CROP_COL, cy + 8, `${c.sell} {coin}`, CROP_GREEN),
  ];
  if (c.harvestXp) out.push(text(cx + CROP_COL, cy + XP_Y, `+${c.harvestXp} {xp_star}`, CROP_XP));   // quick crops: none
  return out;
}

// Desktop (design 3a, owner 2026-10-20): two tinted rows read top to bottom — what you spend and the
// planting XP (red row), the growing time, what you earn and the harvest XP (green row). Numbers are
// left-aligned so coins and XP line up across the 4×2 grid.
const XP_X = 36;          // the handoff's 29 let a 3-digit seed cost ("108 coin") touch the XP
function rowParts(scene, c, cx, cy, pic, text, art = c.id) {
  const g = scene.add.graphics();
  g.fillStyle(c.locked ? 0xefebe4 : 0xfdf0ec).fillRoundedRect(cx - 92, cy - 59, 184, 46, 10);
  g.fillStyle(c.locked ? 0xeceee8 : 0xeef7e8).fillRoundedRect(cx - 92, cy + 7, 184, 46, 10);
  g.fillStyle(0xd9bf8f).fillRect(cx - 80, cy - 13, 2, 20);
  const out = [g,
    pic(cx - 63, cy - 36, seedlingKey(art), 58, 42),
    text(cx - 28, cy - 36, `${c.seed} {coin}`, CROP_RED, 16, true, 0),
    text(cx + XP_X, cy - 36, `+${c.xp} {xp_star}`, CROP_XP, 16, true, 0),
    text(cx - 76, cy - 3, `{timer} ${humanizeRemaining(c.growSeconds)}`, '#6b5a44', 12, true, 0),
    pic(cx - 63, cy + 30, readyKey(art), 58, 44),
    text(cx - 28, cy + 30, `${c.sell} {coin}`, CROP_GREEN, 16, true, 0),
  ];
  if (c.harvestXp) out.push(text(cx + XP_X, cy + 30, `+${c.harvestXp} {xp_star}`, CROP_XP, 16, true, 0));   // quick crops: slot left empty
  return out;
}

// A thin arrow in the card-border tan (drawn: the {arrow_right} icon is the big green page arrow).
function arrow(scene, x, y) {
  const n = fnum(7), g = scene.add.graphics({ x, y });
  g.lineStyle(fnum(3), 0xc9a46a).lineBetween(-n, 0, n - 2, 0);
  g.fillStyle(0xc9a46a).fillTriangle(n + 2, 0, n - 4, -n + 2, n - 4, n - 2);
  return g;
}

export async function openFarmMarket(scene, { selected = null, tab: startTab = 'wszystko', onBuyTool = null, onBuyKonewka = null, onRefuel = null, onUseCan = null, onBuyObject = null, onBuyAnimal = null, onBuyFood = null } = {}) {
  const catalog = gameState.raw?.catalog?.farm ?? {};
  // Lock BEFORE the icons download: a second tap while they load must not open a second market.
  lockInput(scene);
  await ensureLoaded(scene, [...Object.keys(catalog.crops ?? {}).flatMap((id) => [seedlingKey(id), readyKey(id)]),
    ...Object.entries(catalog.crops ?? {}).flatMap(([id, c]) => (c.colors ?? []).slice(1).flatMap((col) => [seedlingKey(`${id}_${col}`), readyKey(`${id}_${col}`)])), ...TOOLS.flatMap((t) => [vehicleKey(t, 2), vehicleKey(t, 4)])]).catch(() => {});
  return new Promise((resolve) => {
    const layer = scene.add.container(0, 0).setDepth(2000).setScrollFactor(0);
    const gate = tapGate();                          // one card action at a time; none after close
    // Food is bought ten or more at a time (owner 2026-10-28): a food tap never holds the gate, it joins a queue that
    // buys one after another, each shown as it lands, and the cards redraw once the burst is through (a redraw mid-
    // burst would swallow the tap in progress). Other cards still wait while one acts.
    const foodQueue = createBuyQueue({
      buy: (t) => onBuyFood?.(t.item),
      onEach: (t, ok) => { if (ok && !gate.closed) floatUp(t.x, t.y, `+1 ${t.name}`); },
      onDrain: () => { if (!gate.closed) draw(); },
    });
    const foodGate = { begin: () => !gate.busy && !gate.closed, end() {}, get closed() { return gate.closed; } };
    let offKeys = () => {}, untrack = () => {};
    const done = (value) => { if (gate.closed) return; gate.close(); offKeys(); untrack(); unlockInput(scene); layer.destroy(true); resolve(value); };
    const tryClose = () => { if (!gate.busy) done(null); };
    untrack = trackPanel(scene, 'market', tryClose);
    let tab = startTab, page = null, pages = 1;       // page null: the selected seed's page (marketPage); pages: kept by each draw
    const storage = siteStorage();                   // null where site data is blocked: the badge and colours keep a session copy
    let body = null;

    const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    overlay.on('pointerup', tryClose);
    // The game look (gameUi.js); an invisible rect under the panel keeps taps on it from closing.
    const panelHit = scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, PANEL_W, PANEL_H, 0xffffff, 0.001).setInteractive();
    const panel = gamePanel(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2, PANEL_W, PANEL_H);
    const title = woodTitle(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 316, '{cart} Rynek', { fontSize: 24 });
    layer.add([overlay, panelHit, panel, title]);

    // Two rows of tabs: the crops (Kwiaty after Zboża), then the rest (farmMarket.TAB_ROWS).
    const tabY = GAME_HEIGHT / 2 - 264, TAB_STEP = 150;
    // "Nowość" on a tab until it is opened once (owner 2026-10-20: the Kwiaty tab).
    if (tab === 'kwiaty') markTabSeen('kwiaty', storage);
    const tabButtons = TAB_ROWS.flatMap((row, r) => row.map((t, i) => {
      const x = GAME_WIDTH / 2 + (i - (row.length - 1) / 2) * TAB_STEP;
      let badge = null;
      const bg = gameButton(scene, x, tabY + r * 44, TAB_STEP - 8, 36, t.label, () => {
        tab = t.id; page = null;                      // the seed's page when it is on this tab, else the first
        if (badge) { markTabSeen(t.id, storage); badge.destroy(); badge = null; }
        draw();
      }, { variant: 'secondary', fontSize: fnum(16) });
      layer.add(bg);
      if (tabIsNew(t.id, storage)) {
        badge = iconText(scene, x + (TAB_STEP - 8) / 2 - 4, tabY + r * 44 - 16, 'Nowość',
          { fontFamily: FONT, fontSize: fpx(11), color: '#fff', fontStyle: 'bold', backgroundColor: '#d4503c', padding: { x: 5, y: 2 } })
          .setOrigin(1, 0.5);
        layer.add(badge);
      }
      return { id: t.id, bg };
    }));

    let nav = null;
    const draw = () => {
      drawBody();
      if (!TOUCH_ROW || !body.list.length) return;
      // Phones: scale the cards about their own centre, as big as the panel's middle allows.
      const b = body.getBounds();
      const k = Math.min(MAX_GRID_SCALE, MAX_GRID_W / b.width, MAX_GRID_H / b.height);
      if (k > 1) body.setScale(k).setPosition(b.centerX * (1 - k), b.centerY * (1 - k));
    };
    // A locked flower colour shows on its card while hovered (desktop) or until the next tap (phone);
    // an unlocked one is picked and remembered (Kwiaty §7). One preview at a time across the cards.
    let unpreview = null, previewSeq = 0;
    const colorDot = (x, y, d, { pick, preview, restore }) => {
      const r = fnum(7);
      const dot = scene.add.circle(x, y, r, d.unlocked ? (DOT_HEX[d.color] ?? 0x999999) : 0x9e9e9e).setStrokeStyle(1.5, 0x5a4a3a);
      dot.setInteractive({ hitArea: new Phaser.Geom.Circle(r, r, DOT_HIT), hitAreaCallback: Phaser.Geom.Circle.Contains, useHandCursor: true });
      const show = () => { unpreview?.(); preview(); unpreview = restore; return ++previewSeq; };
      const hide = () => { if (unpreview === restore) { restore(); unpreview = null; } };
      if (d.unlocked) dot.on('pointerdown', pick);
      else if (TOUCH_ROW) {
        dot.on('pointerdown', () => {
          const seq = show();                        // the next tap anywhere puts the picked colour back
          scene.time.delayedCall(0, () => scene.input.once('pointerdown', () => { if (seq === previewSeq) hide(); }));
        });
      } else {
        dot.on('pointerover', show);
        dot.on('pointerout', hide);
      }
      if (d.picked) return [scene.add.circle(x, y, r + fnum(4)).setStrokeStyle(2, 0x5a4a3a), dot];   // a ring round the picked colour
      if (d.unlocked) return [dot];
      return [dot, scene.add.text(x, y, String(d.need), { fontFamily: FONT, fontSize: fpx(10), color: '#fff', fontStyle: 'bold' }).setOrigin(0.5)];
    };
    const drawBody = () => {
      unpreview = null;
      body?.destroy(true);
      nav?.destroy(true);
      body = scene.add.container(0, 0);
      nav = scene.add.container(0, 0);
      layer.add([body, nav]);
      for (const b of tabButtons) b.bg.setVariant(b.id === tab ? 'wood' : 'secondary');
      const raw = gameState.raw ?? {};                // re-read: a purchase changes it while open
      if (tab === 'narzedzia') { drawTools(raw); return; }
      if (tab === 'dekoracje' || tab === 'budowle' || tab === 'drzewa') { drawObjects(raw); return; }
      if (tab === 'zwierzeta') { drawAnimals(raw); return; }
      const view = marketPage(catalog, { level: raw.level ?? 1, mastery: raw.farm?.mastery ?? {}, tab, page, selected, pageSize: PER_PAGE });
      page = view.page; pages = view.pages;
      const x0 = GAME_WIDTH / 2 - (GRID_COLS * CARD_W + (GRID_COLS - 1) * GAP) / 2 + CARD_W / 2;
      const y0 = ROW_Y;
      view.cards.forEach((c, i) => {
        const cx = x0 + (i % GRID_COLS) * (CARD_W + GAP), cy = y0 + Math.floor(i / GRID_COLS) * (CARD_H + GAP);
        const card = gameCard(scene, cx, cy, CARD_W, CARD_H, { grey: c.locked, selected: c.selected });
        const pics = [];                                 // the seedling and ripe pictures, for a colour preview
        const pic = (x, y, key, w, h) => {
          const k = scene.textures.exists(key) ? key : readyKey(c.id);
          const img = scene.textures.exists(k) ? scene.add.image(x, y, k) : scene.add.circle(x, y, 16, CROP_TINT[c.id] ?? 0x999999);
          if (img.type === 'Image') { img.setScale(Math.min(w / img.width, h / img.height)); pics.push({ img, w, h, stage: key.endsWith('_seedling') ? 'seedling' : 'ready' }); }
          return img.setAlpha(c.locked ? 0.35 : 1);
        };
        const text = (x, y, s, color, size = 15, bold = true, ox = 0.5) => iconText(scene, x, y, s,
          { fontFamily: FONT, fontSize: fpx(size), color: c.locked ? CROP_LOCKED : color, fontStyle: bold ? 'bold' : 'normal' }).setOrigin(ox, 0.5);
        // Flower colours (Kwiaty §7): the picked colour's pictures, and dots on the name row.
        const stars = raw.farm?.mastery?.[c.id]?.stars ?? 0;
        const picked = pickedColor(catalog, c.id, stars, storage);
        const dots = colorDots(catalog, c.id, stars, picked);
        const artOf = (color) => cropArtId(c.id, dots.length && color !== dots[0].color ? color : null);
        const name = iconText(scene, dots.length ? cx - CARD_W / 2 + 14 : cx, cy - 76, c.name, { fontFamily: FONT, fontSize: fpx(18), color: '#000', fontStyle: 'bold' })
          .setOrigin(dots.length ? 0 : 0.5, 0.5);
        const parts = [name, ...(TOUCH_ROW ? storyParts(scene, c, cx, cy, pic, text, artOf(picked)) : rowParts(scene, c, cx, cy, pic, text, artOf(picked)))];
        const footText = iconText(scene, cx, cy + CARD_H / 2 - 18, cropCardFoot(c),
          { fontFamily: FONT, fontSize: fpx(15), color: c.locked ? '#777' : '#b8860b', fontStyle: 'bold' }).setOrigin(0.5);
        const look = (color, hint) => {
          for (const p of pics) {
            const k = `farm_crop_${artOf(color)}_${p.stage}`;
            if (p.img.active && scene.textures.exists(k)) p.img.setTexture(k).setScale(Math.min(p.w / p.img.frame.width, p.h / p.img.frame.height));
          }
          if (footText.active) footText.setText(cropCardFoot(c, hint));
        };
        const dotX = (i) => cx + CARD_W / 2 - DOT_RIGHT - (dots.length - 1 - i) * DOT_STEP;
        if (dots.length) name.setScale(fitScale(name.width, dotX(0) - fnum(11) - 4 - (cx - CARD_W / 2 + 14)));   // a long name next to the dots (phones)
        const dotParts = dots.flatMap((d, i) => colorDot(dotX(i), cy - 76, d, {
          pick: () => { if (gate.busy || gate.closed) return; savePickedColor(c.id, d.color, storage); draw(); },
          preview: () => look(d.color, { need: d.need }),
          restore: () => look(picked, null),
        }));
        body.add(card);
        cardFeedback(scene, card, [...parts, footText, ...dotParts], c.locked ? null : () => done(c.id),
          { cursor: toolCursor('plant'), gate });             // picking a seed: the seed bag
      });
      if (view.pages > 1) {
        nav.add(pageArrows(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 4, ARROW_DX, view.page, view.pages, (p) => { page = p; draw(); }));
        nav.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 218, `${view.page + 1} / ${view.pages}`, { fontFamily: FONT, fontSize: fpx(15), color: '#555' }).setOrigin(0.5));
      }
    };
    // A word rising from a card after a purchase that keeps the market open (on `layer`: survives the redraw).
    const floatUp = (x, y, text) => {
      const t = iconText(scene, x, y, text, { fontFamily: FONT, fontSize: fpx(22), color: '#2e8b1e', fontStyle: 'bold', stroke: '#fff', strokeThickness: 5 }).setOrigin(0.5);
      layer.add(t);
      scene.tweens.add({ targets: t, y: y - 50, alpha: 0, duration: 900, ease: 'Quad.easeOut', onComplete: () => t.destroy() });
    };
    const EMOJI = { tractor: '{tractor}', seeder: '{seedling}', harvester: '{wheat}' };
    let confirming = false;
    const objKey = (item) => (item === 'plot' ? 'farm_obj_fence_post' : `farm_obj_${item}`);
    const OBJ_PH = { tree: 'farm_obj_ph_tree', landmark: 'farm_obj_ph_landmark', hay: 'farm_obj_ph_hay', fence: 'farm_obj_ph_fence_rail', decoration: 'farm_obj_ph_decoration' };
    const drawObjects = (raw) => {
      const all = objectCards(raw, tab);
      pages = Math.max(1, Math.ceil(all.length / PER_PAGE));
      page = Math.min(page ?? 0, pages - 1);
      const cards = all.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
      const specs = raw.catalog?.farm?.objects ?? {};
      const x0 = GAME_WIDTH / 2 - (GRID_COLS * CARD_W + (GRID_COLS - 1) * GAP) / 2 + CARD_W / 2;
      const y0 = ROW_Y;
      cards.forEach((c, i) => {
        const cx = x0 + (i % GRID_COLS) * (CARD_W + GAP), cy = y0 + Math.floor(i / GRID_COLS) * (CARD_H + GAP);
        const grey = c.state === 'locked' || c.state === 'poor';
        const card = gameCard(scene, cx, cy, CARD_W, CARD_H, { grey });
        const tex = objectTextureKey(c.item, specs);                       // a tree shows its fruit
        const key = scene.textures.exists(tex) ? tex : OBJ_PH[specs[c.item]?.kind] ?? OBJ_PH.decoration;
        const img = scene.add.image(cx, cy - 45, key);
        const src = img.texture.getSourceImage();
        const k = Math.min(140 / src.width, 80 / src.height); img.setDisplaySize(src.width * k, src.height * k).setAlpha(grey ? 0.45 : 1);
        const name = iconText(scene, cx, cy + 12, c.name, { fontFamily: FONT, fontSize: fpx(17), color: '#000', fontStyle: 'bold' }).setOrigin(0.5);
        const sub = c.item === 'plot' ? 'za kawałek · przeciągnij' : specs[c.item]?.kind === 'tree' ? `${c.w}×${c.w} · ${treeCardInfo(specs[c.item])}` : `${c.w}×${c.w}`;
        const size = iconText(scene, cx, cy + 36, sub, { fontFamily: FONT, fontSize: fpx(14), color: '#333' }).setOrigin(0.5);
        size.setScale(fitScale(size.width, CARD_W - 24));                // "4×4 · owoce co 3d · 1500 · +56"
        const foot = c.state === 'owned' ? 'Masz {check}' : c.state === 'locked' ? `{lock} poz. ${c.level}` : `{coin} ${c.price.toLocaleString('pl-PL')}`;
        const footText = iconText(scene, cx, cy + CARD_H / 2 - 18, foot, { fontFamily: FONT, fontSize: fpx(15), color: grey ? '#777' : '#b8860b', fontStyle: 'bold' }).setOrigin(0.5);
        body.add(card);
        if (c.state === 'owned') { card.add([img, name, size, footText].map((o) => { o.x -= cx; o.y -= cy; return o; })); return; }
        cardFeedback(scene, card, [img, name, size, footText], c.state !== 'buy' ? null : () => {
          if (confirming) return;
          const buy = () => { done(null); onBuyObject?.(c.item); };
          if (specs[c.item]?.kind !== 'landmark') { buy(); return; }
          confirming = true;                                   // a landmark is a big purchase: ask
          showModal(scene, { title: `Kupić ${c.name}?`, body: `za ${c.price.toLocaleString('pl-PL')} {coin}`, confirmLabel: 'Kup' })
            .then((ok) => { confirming = false; if (ok) buy(); });
        }, { gate });
      });
      if (pages > 1) {                                        // the same arrows as the crop pages
        nav.add(pageArrows(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 4, ARROW_DX, page, pages, (p) => { page = p; draw(); }));
        nav.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 218, `${page + 1} / ${pages}`, { fontFamily: FONT, fontSize: fpx(15), color: '#555' }).setOrigin(0.5));
      }
    };
    // Zwierzęta (4c): huts buy → place mode like any object; an animal is bought on the spot
    // (it moves into a free place, or waits in the Schowek) and the market stays open.
    // Each species: its hut, the animal, its food (owner 2026-10-01) — paged like the other tabs.
    const drawAnimals = (raw) => {
      const all = animalCards(raw);
      pages = Math.max(1, Math.ceil(all.length / PER_PAGE));
      page = Math.min(page ?? 0, pages - 1);
      const x0 = GAME_WIDTH / 2 - (GRID_COLS * CARD_W + (GRID_COLS - 1) * GAP) / 2 + CARD_W / 2;
      const y0 = ROW_Y;
      all.slice(page * PER_PAGE, (page + 1) * PER_PAGE).forEach((c, i) => {
        const cx = x0 + (i % GRID_COLS) * (CARD_W + GAP), cy = y0 + Math.floor(i / GRID_COLS) * (CARD_H + GAP);
        const grey = c.state === 'locked' || c.state === 'poor';
        const card = gameCard(scene, cx, cy, CARD_W, CARD_H, { grey });
        const keys = c.kind === 'hut' ? [`farm_obj_${c.item}`] : c.kind === 'food' ? [`food_${c.item}`]
          : [`farm_animal_${c.item}_idle`, `farm_animal_ph_${c.item}`];
        const key = keys.find((k) => scene.textures.exists(k)) ?? OBJ_PH.decoration;
        const img = scene.add.image(cx, cy - 45, key);
        const src = img.texture.getSourceImage();
        const k = Math.min(140 / src.width, 80 / src.height); img.setDisplaySize(src.width * k, src.height * k).setAlpha(grey ? 0.45 : 1);
        const name = iconText(scene, cx, cy + 12, c.name, { fontFamily: FONT, fontSize: fpx(17), color: '#000', fontStyle: 'bold' }).setOrigin(0.5);
        const info = iconText(scene, cx, cy + 44, c.info, { fontFamily: FONT, fontSize: fpx(13), color: '#333', align: 'center', wordWrap: { width: CARD_W - 16 } }).setOrigin(0.5);
        const foot = c.state === 'locked' ? `{lock} poz. ${c.level}` : `{coin} ${c.price.toLocaleString('pl-PL')}`;
        const footText = iconText(scene, cx, cy + CARD_H / 2 - 18, foot, { fontFamily: FONT, fontSize: fpx(15), color: grey ? '#777' : '#b8860b', fontStyle: 'bold' }).setOrigin(0.5);
        body.add(card);
        cardFeedback(scene, card, [img, name, info, footText], c.state !== 'buy' ? null : () => {
          if (c.kind === 'hut') { done(null); onBuyObject?.(c.item); return; }
          if (c.kind === 'food') { foodQueue.add({ item: c.item, name: c.name, x: cx, y: cy - 45 }); return; }
          const ok = onBuyAnimal?.(c.item) !== false;
          draw();
          if (ok) floatUp(cx, cy - 45, `+1 ${c.name}`);          // the market stays open: show it worked
        }, { gate: c.kind === 'food' ? foodGate : gate });
      });
      if (pages > 1) {
        nav.add(pageArrows(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 4, ARROW_DX, page, pages, (p) => { page = p; draw(); }));
        nav.add(iconText(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + 218, `${page + 1} / ${pages}`, { fontFamily: FONT, fontSize: fpx(15), color: '#555' }).setOrigin(0.5));
      }
    };
    const drawTools = (raw) => {
      pages = 1;                                         // one row of tool cards, no pages
      const cards = toolCards(catalog, { level: raw.level ?? 1, tools: raw.farm?.tools ?? {},
        fuelUnits: raw.farm ? fuelNow(raw.farm, catalog.fuel, serverNow()) : 0, money: raw.money ?? 0, cans: raw.farm?.fuel_cans ?? 0,
        konewka: !!raw.farm?.konewka });
      // Five cards since the Konewka (2026-10-27): a little narrower, so the row still fits the panel.
      const n = cards.length, CW = n > COLS ? 196 : CARD_W, G = n > COLS ? 10 : GAP;
      const x0 = GAME_WIDTH / 2 - (n * CW + (n - 1) * G) / 2 + CW / 2;
      const cy = GAME_HEIGHT / 2 + 4;
      cards.forEach((c, i) => {
        const cx = x0 + i * (CW + G);
        const grey = c.state === 'locked' || c.state === 'poor';
        const card = gameCard(scene, cx, cy, CW, CARD_H + 60, { grey });
        let icon;
        if (c.kind === 'konewka') {                      // its picture (assetManifest FARM_KONEWKA); the drop until it lands
          icon = scene.textures.exists('farm_tool_konewka')
            ? scene.add.image(cx, cy - 56, 'farm_tool_konewka').setDisplaySize(100, 100 * scene.textures.get('farm_tool_konewka').getSourceImage().height / scene.textures.get('farm_tool_konewka').getSourceImage().width)
            : drawDrop(scene.add.graphics().setPosition(cx, cy - 50), 3);
        }
        else if (c.kind === 'tool') {
          const key = vehicleTexture(scene, c.id, c.size);
          icon = scene.textures.exists(key)
            ? scene.add.image(cx, cy - 60, key).setDisplaySize(150, 150 * scene.textures.get(key).getSourceImage().height / scene.textures.get(key).getSourceImage().width)
            : iconText(scene, cx, cy - 60, EMOJI[c.id], { fontSize: '54px' }).setOrigin(0.5);
        } else icon = iconText(scene, cx, cy - 60, '{fuel}', { fontSize: '54px' }).setOrigin(0.5);
        icon.setAlpha(grey ? 0.45 : 1);
        const title = c.kind === 'tool' ? c.name : c.kind === 'konewka' ? 'Konewka' : 'Paliwo';
        const sub = c.kind === 'tool' ? `${c.size}×${c.size}` : c.kind === 'konewka' ? 'Podlewa 2×2 pola u znajomych' : `${c.units} / ${c.tank}`;
        const name = iconText(scene, cx, cy + 12, title, { fontFamily: FONT, fontSize: fpx(20), color: '#000', fontStyle: 'bold' }).setOrigin(0.5);
        const info = iconText(scene, cx, cy + (c.kind === 'konewka' ? 52 : 40), sub, { fontFamily: FONT, fontSize: fpx(16), color: '#333', align: 'center', wordWrap: { width: CW - 16 } }).setOrigin(0.5);
        let foot;
        if (c.kind === 'konewka') foot = c.state === 'owned' ? 'Masz {check}' : `{coin} ${c.price}`;
        else if (c.kind === 'tool') {
          foot = c.state === 'owned' ? 'Masz {check}' : c.state === 'locked' ? `{lock} poz. ${c.level}`
            : c.owned ? `Ulepsz do 4×4 — {coin} ${c.price}` : `{coin} ${c.price}`;
        } else foot = c.state === 'full' ? 'Pełny bak {check}' : `Zatankuj — {coin} ${c.price}`;
        const footText = iconText(scene, cx, cy + CARD_H / 2 + 6, foot, { fontFamily: FONT, fontSize: fpx(15), color: grey ? '#777' : '#b8860b', fontStyle: 'bold', align: 'center', wordWrap: { width: CW - 16 } }).setOrigin(0.5);
        body.add(card);
        if (c.kind === 'fuel' && c.cans > 0) {
          // friends' canisters: their own small button on the card (greyed while one would not fit whole)
          const lbl = iconText(scene, cx, cy + 60, `{canister} ${c.cans} ${plural(c.cans, 'kanister', 'kanistry', 'kanistrów')}`, { fontFamily: FONT, fontSize: fpx(15), color: '#333' }).setOrigin(0.5);
          const btn = gameButton(scene, cx, cy + 84, 150, 30, '{canister} Użyj', () => { onUseCan?.(); draw(); }, { variant: 'primary', fontSize: fnum(15), enabled: c.canUsable });
          body.add([lbl, btn]);
          footText.setY(cy + 114);                // the refill line goes under the canister button
        }
        const canTap = c.kind === 'tool' ? (c.state === 'buy' || c.state === 'upgrade') : c.state === 'buy';   // konewka / fuel: 'buy' 
        const parts = [icon, name, info, footText];
        if (!canTap && !grey) { card.add(parts.map((o) => { o.x -= cx; o.y -= cy; return o; })); return; }
        {
          cardFeedback(scene, card, parts, !canTap ? null : () => {
            if (c.kind === 'fuel') { onRefuel?.(); draw(); floatUp(cx, cy - 60, 'Zatankowano {fuel}'); return; }
            if (c.kind === 'konewka') {                // bought once, so ask first, like a machine
              if (confirming) return;
              confirming = true;
              showModal(scene, { title: 'Kupić Konewkę?', body: `za ${c.price} {coin}`, confirmLabel: 'Kup' })
                .then((ok) => { confirming = false; if (ok) onBuyKonewka?.(); draw(); });
              return;
            }
            // A vehicle is the farm's biggest purchase and can't be sold back: ask first. The
            // modal's overlay also swallows a double tap, so one tap never buys two sizes.
            if (confirming) return;
            confirming = true;
            showModal(scene, {
              title: c.owned ? `Ulepszyć ${c.name} do ${c.size}×${c.size}?` : `Kupić ${c.name} ${c.size}×${c.size}?`,
              body: `za ${c.price} {coin}`,
              confirmLabel: c.owned ? 'Ulepsz' : 'Kup',
            }).then((ok) => { confirming = false; if (ok) onBuyTool?.(c.id); draw(); });
          }, { gate });
        }
      });
    };
    // Keyboard (owner 2026-10-02): ←/→ pages, Esc closes, Tab / Shift+Tab the next / previous tab. Caught on the
    // document in the capture phase, before the browser (Tab moved the focus, arrows scrolled the page) and before
    // Phaser's own listener on window: the farm never sees these keys while the market is open — an Esc here does
    // not also cancel its work queue. Not while typing in the chat; ignored while a confirm or a purchase is busy.
    const MARKET_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Escape', 'Tab']);
    const onKey = (ev) => {
      if (!MARKET_KEYS.has(ev.key) || gate.closed) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      ev.preventDefault(); ev.stopPropagation();
      if (confirming || gate.busy) return;
      const r = marketKey(ev.key, ev.shiftKey, { tab, page, pages });
      if (!r) return;
      if (r === 'close') { tryClose(); return; }
      tab = r.tab; page = r.page; draw();
    };
    document.addEventListener('keydown', onKey, true);
    offKeys = () => document.removeEventListener('keydown', onKey, true);
    draw();

    layer.add(gameButton(scene, GAME_WIDTH / 2, GAME_HEIGHT / 2 + PANEL_H / 2 - 50, 200, 46, 'Zamknij', tryClose, { variant: 'wood', fontSize: fnum(18) }));   // inside the panel's bottom edge
  });
}
